/**
 * A picture small enough to upload. The proxy in front of the API refuses bodies over 1 MB and
 * PHP files over 2 MB, while a phone photo is often 3-8 MB. A file already small enough goes as it
 * is (a PNG keeps its transparency); a bigger one is redrawn as a JPEG, at most MAX_SIDE pixels on
 * its long side, at the best quality that fits.
 */
const MAX_BYTES = 900 * 1024;
const MAX_SIDE = 2400; // the largest picture made from an upload is a 1920 x 800 app banner
const QUALITIES = [0.85, 0.75, 0.65];
const SMALLER = 0.8; // when no quality fits, the next try is at least this much smaller

/** The picture, turned the way the camera held it. */
async function decode(file) {
  if (window.createImageBitmap) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) {
      // Some browsers take no options here: an <img> below.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const jpeg = (canvas, quality) => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

const jpegName = (name) => String(name || "image").replace(/\.[^./]*$/, "") + ".jpg";

/** The file itself when it is small enough, else a smaller JPEG copy (the file itself if none fits). */
export async function shrinkImage(file) {
  if (!file || file.size <= MAX_BYTES) return file;
  const img = await decode(file);
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    for (let tries = 0; tries < 4; tries++) {
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      // A JPEG has no transparency: clear parts of a PNG turn white, not black.
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      let blob = null;
      for (const quality of QUALITIES) {
        blob = await jpeg(canvas, quality);
        if (blob && blob.size <= MAX_BYTES) return new File([blob], jpegName(file.name), { type: "image/jpeg" });
      }
      // The bytes follow the pixel count: both sides shrink by the square root of how far over it was.
      scale *= blob ? Math.min(SMALLER, 0.95 * Math.sqrt(MAX_BYTES / blob.size)) : SMALLER;
    }
    return file;
  } finally {
    if (img.close) img.close();
  }
}
